/**
 * Tags in a rule, pure: a surface's enabled tags as the Flow Optimizer field
 * picker's tags (their own section — "Hour tag", "Text tag", "Gateway tag",
 * "List tag" — in their colours), and the names a rule tests that no enabled
 * tag has — those conditions are unknown, so the rule does nothing.
 * For Media Devoted | Created By Ciro Autiero
 */
import { ruleTagNames, type DropGenRule, type DropGenTagOption } from '@trafficpulse/shared';

import type { GroupTag } from '@/features/flow-optimizer/tabs/exclude/rule-condition/kind/condition-kind';

export const HOUR_TAG_GROUP = 'Hour tag';

/**
 * The tag's colour as a dot emoji — the picker is the browser's own dropdown,
 * which ignores text colours (macOS), so the colour rides the option's label
 * (user, 12/10/2026: "add the colors please"). Nearest of 🔴 🟠 🟡 🟢 🔵 🟣 🟤 ⚫ ⚪;
 * no colour = the primary blue (the dot the rows draw).
 */
function hueOf(r: number, g: number, b: number, max: number, d: number): number {
  let hue = (r - g) / d + 4;
  if (max === r) hue = ((g - b) / d) % 6;
  else if (max === g) hue = (b - r) / d + 2;
  return (hue * 60 + 360) % 360;
}

export function colorDot(hex: string | null | undefined): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
  if (!m) return '🔵';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const light = (max + min) / 2;
  const sat = max === min ? 0 : (max - min) / (1 - Math.abs(2 * light - 1));
  if (light < 0.2) return '⚫';
  if (sat < 0.2) return light < 0.4 ? '⚫' : '⚪';
  const h = hueOf(r, g, b, max, max - min);
  if (h < 15 || h >= 345) return light < 0.3 ? '🟤' : '🔴';
  if (h < 40) return light < 0.3 ? '🟤' : '🟠';
  if (h < 65) return '🟡';
  if (h < 185) return '🟢';
  if (h < 260) return '🔵';
  return '🟣';
}

export const tagOptions = (catalog: DropGenTagOption[] | undefined, group: string): GroupTag[] =>
  (catalog ?? []).map((t) => ({ name: t.name, color: t.color, emoji: colorDot(t.color), group }));

export const hourTagOptions = (catalog: DropGenTagOption[] | undefined): GroupTag[] => tagOptions(catalog, HOUR_TAG_GROUP);

export const isMissingTag = (catalog: DropGenTagOption[] | undefined, name: string): boolean =>
  catalog !== undefined && !catalog.some((t) => t.name === name);

/** The tag names this rule tests that no enabled tag has (none while the catalog loads). */
export function missingTagsIn(rule: DropGenRule, catalog: DropGenTagOption[] | undefined): string[] {
  return ruleTagNames([{ ...rule, enabled: true }]).filter((name) => isMissingTag(catalog, name));
}
